import { FeedbackControllerReportBugRequest } from '@cratehackers/api-client';
import {
  IonAlert,
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonInput,
  IonPage,
  IonRow,
  IonText,
  IonTextarea,
  IonToast,
} from '@ionic/react';
import {
  bugOutline,
  checkmarkCircleOutline,
  informationCircleOutline,
  refreshCircle,
} from 'ionicons/icons'; // Import the checkmark and refresh icons
import React, { useEffect, useRef, useState } from 'react';
import { config } from '../../config';
import { on, run } from '../../helper/electron';
import ApiService from '../../services/Api.service';
import {
  KnownIssuesApi,
  KnownIssueDto,
} from '../../services/KnownIssuesApi.service';
import isElectron from '../../utils/isElectron';
import style from '../folder-flattener/folder-flattener.module.scss';
import feedbackStyles from './feedback.module.scss';

const BugReportPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false); // State for showing the checkmark
  const [description, setDescription] = useState('');
  const [os, setOs] = useState('');
  const [osVersion, setOsVersion] = useState('');
  const [osArch, setOsArch] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [seratoApiVersion, setSeratoApiVersion] = useState<{
    version: string | null;
    gitCommit: string | null;
    buildTime: string | null;
  } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColour, setToastColour] = useState('');
  const [knownIssues, setKnownIssues] = useState<KnownIssueDto[]>([]);
  const [knownIssuesLoading, setKnownIssuesLoading] = useState(true);
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);

  // Timeout ref for cleanup
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const apiClient = ApiService.getApiClient();

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      // Build internal diagnostics header (hidden from user)
      let fullDescription = description;
      if (seratoApiVersion?.version) {
        const diagnosticsHeader = `[Internal] serato-api: v${seratoApiVersion.version} (${seratoApiVersion.gitCommit || 'unknown'})\n\n`;
        fullDescription = diagnosticsHeader + description;
      }

      const bugReportRequest: FeedbackControllerReportBugRequest = {
        bugReportDto: {
          description: fullDescription,
          os,
          osVersion,
          osArch,
          appVersion,
        },
      };

      await apiClient.feedbackControllerReportBug(bugReportRequest);

      setShowSuccess(true); // Show success checkmark

      // Clear any existing timeout and set new one with cleanup
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(
        () => setShowSuccess(false),
        10000,
      );

      clearForm(); // Clear the form
      setToastMessage(
        'Bug report has been sent! Thank you for helping make Crate Hackers better!',
      );
      setToastColour('success');
      setShowToast(true); // Show success toast
    } catch (error) {
      console.error('Error submitting bug report:', error);
      setToastMessage('An error occurred. Please try again later.');
      setToastColour('danger');
      setShowToast(true); // Show danger toast
    } finally {
      setIsLoading(false);
    }
  };

  const clearForm = () => {
    setDescription('');
  };

  useEffect(() => {
    // Load known issues
    KnownIssuesApi.listActive()
      .then((data) => setKnownIssues(data))
      .catch((error) => console.error('Error loading known issues:', error))
      .finally(() => setKnownIssuesLoading(false));

    if (isElectron()) {
      // Fetch App Version
      on('getAppVersion', (response: string) => {
        if (response) {
          setAppVersion(response);
        } else {
          console.error('App Version is not defined.');
        }
      });
      run('getAppVersion');

      // Fetch OS information
      on(
        'getOSInfo',
        (response: { os: string; osVersion: string; osArch: string }) => {
          if (response) {
            setOs(response.os);
            setOsVersion(response.osVersion);
            setOsArch(response.osArch);
          } else {
            console.error('OS information is not defined.');
          }
        },
      );
      run('getOSInfo');

      // Fetch serato-api binary version for internal diagnostics
      on(
        'getSeratoApiVersion',
        (response: {
          version: string | null;
          gitCommit: string | null;
          buildTime: string | null;
        }) => {
          if (response) {
            setSeratoApiVersion(response);
          }
        },
      );
      run('getSeratoApiVersion');
    } else {
      // Set fallback values for non-Electron environments
      setAppVersion(config.VERSION || 'unknown');
      setOs('unknown');
      setOsVersion('unknown');
      setOsArch('unknown');
    }
  }, []);

  return (
    <IonPage className={feedbackStyles['page']}>
      <IonContent fullscreen>
        <IonGrid className={feedbackStyles['grid']}>
          <IonRow className={feedbackStyles['header-row']}>
            <IonCol>
              <div
                className={style['page-header']}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '1rem',
                }}
              >
                <h1
                  className={style['title']}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: 0,
                  }}
                >
                  <IonIcon icon={bugOutline} />
                  Report A Bug
                </h1>
              </div>
            </IonCol>
          </IonRow>

          <IonRow style={{ justifyContent: 'center' }}>
            <IonCol size="12" sizeMd="8">
              <IonCard
                style={{
                  '--background': '#3a2e00',
                  marginBottom: '0.5rem',
                }}
              >
                <IonCardContent>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                    }}
                  >
                    <IonIcon
                      icon={informationCircleOutline}
                      color="warning"
                      style={{
                        fontSize: '1.5rem',
                        flexShrink: 0,
                        marginTop: '2px',
                      }}
                    />
                    <div>
                      <IonText
                        style={{
                          fontSize: '0.95rem',
                          lineHeight: '1.6',
                          color: 'var(--ion-color-dark)',
                        }}
                      >
                        <p style={{ margin: '0 0 8px 0' }}>
                          This form is used to submit bug reports to our
                          development team for internal tracking. Reports
                          submitted here help us identify and fix issues, but we
                          do not respond to submissions through this form.
                        </p>
                        <p style={{ margin: 0 }}>
                          If you need support, have questions, or require a
                          response, please contact{' '}
                          <a
                            href="mailto:help@cratehackers.com"
                            style={{ color: 'var(--ion-color-primary)' }}
                          >
                            help@cratehackers.com
                          </a>
                          .
                        </p>
                      </IonText>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>

          {/* Known Issues and Form Side by Side */}
          <IonRow
            className={`${feedbackStyles['form-row']} ion-justify-content-center`}
          >
            {/* Known Issues Section */}
            {!knownIssuesLoading && knownIssues.length > 0 && (
              <IonCol size="12" sizeMd="6">
                <IonCard>
                  <IonCardContent>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '16px',
                      }}
                    >
                      <IonIcon
                        icon={informationCircleOutline}
                        color="primary"
                        size="large"
                      />
                      <h2 style={{ margin: 0, fontSize: '1.2rem' }}>
                        Known Issues
                      </h2>
                    </div>
                    {knownIssues.map((issue) => (
                      <div
                        key={issue.id}
                        style={{
                          marginBottom: '16px',
                          paddingBottom: '16px',
                          borderBottom:
                            issue.id !== knownIssues[knownIssues.length - 1].id
                              ? '1px solid var(--ion-color-light-shade)'
                              : 'none',
                        }}
                      >
                        <h3
                          style={{
                            margin: '0 0 8px 0',
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: 'var(--ion-color-dark)',
                          }}
                        >
                          {issue.title}
                        </h3>
                        <IonText
                          color="medium"
                          style={{
                            fontSize: '0.9rem',
                            lineHeight: '1.5',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {issue.content}
                        </IonText>
                      </div>
                    ))}
                  </IonCardContent>
                </IonCard>
              </IonCol>
            )}

            {/* Bug Report Form */}
            <IonCol
              size="12"
              sizeMd={knownIssues.length > 0 ? '6' : '8'}
              sizeLg={knownIssues.length > 0 ? '6' : '6'}
              className={feedbackStyles['form-column']}
            >
              {isElectron() && (
                <>
                  <div className={feedbackStyles['form-field']}>
                    <IonInput
                      className={feedbackStyles['input']}
                      labelPlacement="floating"
                      fill="outline"
                      value={os}
                      readonly
                      onIonChange={(e) => setOs(e.detail.value!)}
                      required
                    >
                      <div slot="label">Operating System</div>
                    </IonInput>
                  </div>
                  <div className={feedbackStyles['form-field']}>
                    <IonInput
                      className={feedbackStyles['input']}
                      labelPlacement="floating"
                      fill="outline"
                      value={osVersion}
                      readonly
                      onIonChange={(e) => setOsVersion(e.detail.value!)}
                      required
                    >
                      <div slot="label">OS Version</div>
                    </IonInput>
                  </div>
                  <div className={feedbackStyles['form-field']}>
                    <IonInput
                      className={feedbackStyles['input']}
                      labelPlacement="floating"
                      fill="outline"
                      value={osArch}
                      readonly
                      onIonChange={(e) => setOsArch(e.detail.value!)}
                      required
                    >
                      <div slot="label">OS Platform</div>
                    </IonInput>
                  </div>
                </>
              )}
              <div className={feedbackStyles['form-field']}>
                <IonInput
                  className={feedbackStyles['input']}
                  labelPlacement="floating"
                  fill="outline"
                  value={appVersion}
                  readonly
                  onIonChange={(e) => setAppVersion(e.detail.value!)}
                  required
                >
                  <div slot="label">App Version</div>
                </IonInput>
              </div>
              <div className={feedbackStyles['form-field']}>
                <IonTextarea
                  className={feedbackStyles['input']}
                  labelPlacement="floating"
                  fill="outline"
                  autoGrow={true}
                  value={description}
                  onIonChange={(e) => setDescription(e.detail.value!)}
                  required
                >
                  <div slot="label">Describe the bug you experienced.</div>
                </IonTextarea>
              </div>
              <IonButton
                expand="block"
                onClick={() => setShowConfirmAlert(true)}
                disabled={isLoading}
                className={feedbackStyles['submit-button']}
              >
                {isLoading ? (
                  <IonIcon slot="start" icon={refreshCircle} />
                ) : showSuccess ? (
                  <IonIcon
                    slot="start"
                    icon={checkmarkCircleOutline}
                    color="success"
                  />
                ) : (
                  'Submit Bug Report'
                )}
              </IonButton>
              <IonAlert
                isOpen={showConfirmAlert}
                onDidDismiss={() => setShowConfirmAlert(false)}
                header="Before you submit"
                message="Submissions through this form will not receive a response. For assistance or urgent issues, contact help@cratehackers.com."
                buttons={[
                  {
                    text: 'Cancel',
                    role: 'cancel',
                  },
                  {
                    text: 'Submit',
                    handler: () => {
                      handleSubmit();
                    },
                  },
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
        {showToast && toastMessage && toastColour && (
          <IonToast
            isOpen={showToast}
            onDidDismiss={() => setShowToast(false)}
            message={toastMessage}
            duration={10000} // 10 seconds
            color={toastColour}
            position="top"
          />
        )}
      </IonContent>
    </IonPage>
  );
};

export default BugReportPage;
